import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { Schema } from 'effect';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { SchemaUtils } from '@listening/api-contract/schema-utils';
import { PostPurchaseCarouselContent } from '@/components/astro/features/post-purchase-setup/carousel-content.island';
import { PostPurchaseNavHeader } from '@/components/astro/features/post-purchase-setup/nav-header.island';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { cookieTrackersPurchase } from '@/seo/cookie-trackers-purchase';
import { normalizeCurrency } from '@/seo/cookie-trackers/purchase/currency';
import * as m from '@/paraglide/messages';
import { rootLogger } from '@/lib/root-logger';
import { callApiPromise } from '@/runtimes/get-runtime';
import {
  arrivedFromStripe,
  NO_PURCHASE_PIXELS,
  purchasePixelsFrom,
  type PurchasePixelFields,
} from '@/lib/checkout/post-purchase-pixels';
import { seo } from '@/seo/seo';
import { useCheckoutReturnCleanup } from '@/services/subscription-service';

const moduleLogger = rootLogger.child({ module: 'PostPurchaseLoader' });

// Stripe `confirmPayment()` / `confirmSetup()` redirect the browser to this
// route with `?payment_intent=…&redirect_status=…` after the payment action.
//
// **This is the destination for EVERY checkout that confirms with Stripe, not
// just 3DS and wallets.** `check-out-form.tsx` passes no `redirect` option, so
// Stripe uses its default `redirect: 'always'`, which navigates the whole
// browser to `return_url` for every payment method including plain cards.
// (Opting out is `redirect: 'if_required'`, which this repo has never used.)
// The `localizedHomePath` returned after `confirmPayment` there is therefore
// effectively unreachable on success — the page is already gone.
//
// It is worth stating plainly, because the opposite reading implies
// `/post-purchase` pageviews undercount conversions, which would make every
// funnel and experiment built on that signal look broken. The Phase 0 audit
// measured the opposite (`docs/projects/complete/accounts-and-billing/audit-cookie-trackers.md`:
// `/post-purchase` pageview count matched the subscription count 1:1).
//
// So inbound traffic here is: every purchase that reached Stripe confirmation,
// failed/declined redirect-flow attempts (hence the `redirect_status` gate on
// the pixels), and the e2e test bypass. The one checkout outcome that does not
// arrive is `nothing-to-confirm` — an uncancel, or a subscription that
// activated on a card already on file — which never calls Stripe and goes
// straight to `/app/home`.
const PostPurchaseSearchSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    payment_intent: Schema.optional(Schema.String),
    setup_intent: Schema.optional(Schema.String),
    redirect_status: Schema.optional(
      SchemaUtils.LiteralOrUndefined('succeeded', 'failed', 'pending'),
    ),
  }),
);

// This route lives at the routes root (NOT under _marketing) so the
// `_marketing.tsx` head() — which injects landing-stage trackers like
// TikTok ViewContent and Quora ViewContent — does not merge into this
// page and double-count them.
//
// Site-wide trackers (Meta PageView, Reddit PageVisit, the Google tag,
// Bing, LinkedIn, Clarity) come from `__root.tsx`, which every document
// shares; only the purchase-stage trackers are declared here.
//
// The loader calls `Billing.Confirm` during SSR, and `head()` bakes the
// resulting pixel values into the inline scripts, so they fire when the
// head parses — no useEffect race. Every unknown resolves to no pixel
// (ghost URL, test bypass, decline-redirect, backend error): a
// fabricated conversion value is worse than a missing one.
//
// @see docs/projects/active/audit-cookie-trackers.md
export const Route = createFileRoute('/post-purchase')({
  validateSearch: PostPurchaseSearchSchema,
  loaderDeps: ({ search }) => ({
    paymentIntent: search.payment_intent,
    setupIntent: search.setup_intent,
    redirectStatus: search.redirect_status,
  }),
  loader: async ({ deps, abortController }) => {
    // Stripe payment-intent details for the purchase pixels —
    // conditional on a `payment_intent` or `setup_intent` query param
    // (Stripe redirect flow). Without one of those there is no purchase
    // to confirm (e.g. the e2e test bypass loads this URL bare), so we
    // skip the call.
    //
    // A second `User.Summary` call used to run alongside this one to
    // resolve `customer_id` for the site-wide ProfitWell session;
    // ProfitWell is gone, so the call is too. Note this route is NOT
    // cdn-shared — per-user SSR is legitimate here — that call was
    // simply serving nothing.
    const pixels: PurchasePixelFields = !arrivedFromStripe(deps)
      ? NO_PURCHASE_PIXELS
      : await callApiPromise((api) => api.Billing.Confirm(), {
          signal: abortController.signal,
        })
          .then(purchasePixelsFrom)
          .catch((error: unknown) => {
            // Safe-default: skip every conversion pixel rather than
            // fabricate a value. Log so this stays visible — the
            // `amount_usd` schema rejects non-positive amounts, so a
            // backend bug emitting `amount_usd: 0` would silently
            // suppress `trial_started` here too, and we'd rather see
            // it in logs than discover it via missing trial pixels.
            moduleLogger.error(
              { error, deps },
              'Billing.Confirm loader call failed — skipping conversion + trial pixels',
            );
            return NO_PURCHASE_PIXELS;
          });

    return pixels;
  },
  head: ({ loaderData }) => ({
    meta: [
      ...seo({
        title: m.meta_post_purchase_title(),
        description: m.meta_post_purchase_description(),
      }),
    ],
    scripts: [
      ...cookieTrackersPurchase({
        amountUsd: loaderData?.amountUsd ?? null,
        currency: loaderData?.currency ?? null,
        trialStarted: loaderData?.trialStarted ?? false,
      }),
    ],
  }),
  component: Page,
});

function Page() {
  useCheckoutReturnCleanup();

  // Typed purchase signal for funnels/experiments, under the SAME gate as
  // the conversion pixels in head(): only a Stripe-confirmed outcome
  // reports. Declined redirects, ghost URLs, and backend errors resolve to
  // { amountUsd: null, trialStarted: false } in the loader and emit nothing.
  const { amountUsd, currency, trialStarted } = Route.useLoaderData();
  const { capture } = useAnalytics();
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current || (amountUsd === null && !trialStarted)) return;
    reported.current = true;
    capture('purchase_completed', {
      amount_usd: amountUsd,
      currency: normalizeCurrency(currency),
      trial_started: trialStarted,
    });
  }, [amountUsd, currency, trialStarted, capture]);

  return (
    <>
      <h1 className='sr-only'>{m.meta_post_purchase_title()}</h1>

      <PostPurchaseNavHeader />
      <main className='flex-1'>
        <PostPurchaseCarouselContent />
      </main>
      <Footer />
    </>
  );
}
